import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import ReportPage, { ReportDataProps, ReportTitleProps } from "@components/dynamic/ReportPage";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { useAppSelector } from "@/store/hooks";
import {
  EDOCUMENT_REPORT_ALL_KINDS,
  EDOCUMENT_REPORT_EXCEL_TITLES,
  buildEDocumentReportRow,
  fetchAllEDocumentReportRows,
  resolveEDocumentKindLabel,
} from "@utils/EDocumentReport";

/** Anchos de las columnas del PDF; el Excel solo usa las etiquetas. */
const COLUMN_WIDTHS = ["12%", "20%", "24%", "24%", "10%", "10%"];

const REPORT_TITLES: ReportTitleProps[] = EDOCUMENT_REPORT_EXCEL_TITLES.map((label, index) => ({
  label,
  width: COLUMN_WIDTHS[index],
  isNumeric: label === "Total",
}));

/**
 * Exportacion a Excel del reporte de documentos electronicos. Recorre todas las paginas
 * del indice unificado para el tipo seleccionado, porque el listado en pantalla solo trae
 * la pagina visible.
 */
const EDocumentsReportExcel = () => {
  const { kind = EDOCUMENT_REPORT_ALL_KINDS } = useParams();
  const suscription = useAppSelector((state) => state.suscription.suscription || {});
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  useEffect(() => {
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false });
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      const { rows, truncated } = await fetchAllEDocumentReportRows(kind);
      setIsTruncated(truncated);
      setReportData(rows.map((row) => ({ sourceData: row, values: buildEDocumentReportRow(row) })));
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true });
    }
  };

  const renderSubtitle = (): string => {
    const kindLabel = kind === EDOCUMENT_REPORT_ALL_KINDS
      ? "Todos los documentos"
      : resolveEDocumentKindLabel(kind);

    return isTruncated
      ? `${kindLabel} | Se alcanzo el maximo de documentos exportables`
      : kindLabel;
  };

  return (
    <ReportPage
      loading={isLoading}
      excelExport
      suscription={suscription}
      reportConfig={{
        title: "Reporte de Documentos Electronicos",
        subtitle: renderSubtitle(),
        xmlFileName: "documentos-electronicos",
        date: `${moment().format()}`,
      }}
      reportData={reportData}
      reportTitles={REPORT_TITLES}
    />
  );
};

export default EDocumentsReportExcel;
